import React from 'react'
import { Route, Switch, useHistory } from 'react-router'

import { useCheckout } from '../../state'
import {
  DocumentStep,
  NameStep,
  CompanyNameStep,
  TradeNameStep,
  CorporatePhoneStep,
  CorporateDocumentStep,
  EmailStepContainer,
  IdentificationStep,
  ClientProfileContainer,
  PhoneStep,
  StateInscriptionStep,
} from '../../components'
import { CheckoutRoute, PURCHASE_FLOW_ORIGIN_HASH } from '../../constants'
import { isEditMode } from '../../utils/verifyEditionMode'
import { getOrigin } from '../../utils/getOrigin'
import { isClientProfileComplete } from '../../utils'
import type { OrderForm } from '../../../../core/requests/checkout'
import { getClientDataStateFromOrderForm } from '../../utils/getClientDataStateFromOrderForm'
import type { ClientDataState } from '../../typings'
import { checkNameIsAnonymous } from '../../utils/checkNameIsAnonymous'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'
import { useIdentificationStep } from '../../hooks'

export const ClientPickupFromStoreScreen = () => {
  const history = useHistory()

  const {
    checkout: { clientData },
    setClientDataState,
    setClientDataPhone,
    setClientDataName,
    setClientDataDocument,
    setClientDataEmail,
    setClientDataCompanyName,
    setClientDataTradeName,
    setClientDataCorporatePhone,
    setClientDataCorporateDocument,
    clearClientDataFromClientDataState,
    setClientDataStateIncription,
    setClientDataIsCorporate,
  } = useCheckout()

  const isEditing = isEditMode()
  const origin = getOrigin() ?? ''
  const initialName = checkNameIsAnonymous(clientData.name)
    ? ''
    : clientData.name

  const identificationStep = useIdentificationStep()

  const handleNameSubmit = (name: ClientDataState['name']) => {
    setClientDataName(name)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.PickupPhone
    )
  }

  const handleDocumentSubmit = (document: ClientDataState['document']) => {
    setClientDataDocument(document)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.PickupName
    )
  }

  const handleConfirmClientSubmit = () => {
    history.push('../order-summary')
  }

  const handleCompanyNameSubmit = (
    companyName: ClientDataState['companyInformation']['companyName']
  ) => {
    setClientDataCompanyName(companyName)

    history.push(
      isEditing
        ? `../${CheckoutRoute.PickupClientProfile}`
        : CheckoutRoute.PickupTradeName
    )
  }

  const handleTradeNameSubmit = (
    tradeName: ClientDataState['companyInformation']['tradeName']
  ) => {
    setClientDataTradeName(tradeName)

    history.push(
      isEditing
        ? `../${CheckoutRoute.PickupClientProfile}`
        : CheckoutRoute.PickupCorporatePhone
    )
  }

  const handleCorporatePhoneSubmit = (
    corporatePhone: ClientDataState['companyInformation']['corporatePhone']
  ) => {
    setClientDataCorporatePhone(corporatePhone)

    history.push(
      isEditing
        ? `../${CheckoutRoute.PickupClientProfile}`
        : `${CheckoutRoute.PickupCorporateDocument}`
    )
  }

  const handleCorporateDocumentSubmit = (
    corporateDocument: ClientDataState['companyInformation']['corporateDocument']
  ) => {
    setClientDataCorporateDocument(corporateDocument)

    history.push(
      isEditing
        ? `../${CheckoutRoute.PickupClientProfile}`
        : `${CheckoutRoute.PickupStateInscription}`
    )
  }

  const handleEmailSubmit = async (orderForm: OrderForm | null) => {
    if (!orderForm) {
      return
    }

    const submittedEmail = orderForm.clientProfileData?.email ?? ''

    if (!isClientProfileComplete(orderForm.clientProfileData)) {
      if (isEditing && clientData.email !== submittedEmail) {
        clearClientDataFromClientDataState()
      }

      setClientDataEmail(submittedEmail)
      const nextStep = orderForm?.clientProfileData?.document
        ? CheckoutRoute.PickupName
        : CheckoutRoute.PickupDocument

      history.push(nextStep)

      return
    }

    setClientDataState(getClientDataStateFromOrderForm(orderForm))

    history.push(CheckoutRoute.OrderSummary)
  }

  const handleStateInscriptionSubmit = (
    stateInscription: ClientDataState['companyInformation']['stateInscription']
  ) => {
    setClientDataStateIncription(stateInscription)
    setClientDataIsCorporate(true)

    history.push(CheckoutRoute.PickupClientProfile)
  }

  const handlePhoneSubmit = (phone: ClientDataState['phone']) => {
    setClientDataPhone(phone)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.PickupClientProfile
    )
  }

  const handleIdentifySubmit = (
    submittedOrderForm: OrderForm | null,
    identification: string | null,
    identificationType: string
  ) => {
    identificationStep.handleStepSubmit(
      submittedOrderForm,
      identification,
      identificationType,
      {
        document: CheckoutRoute.PickupDocument,
        email: CheckoutRoute.PickupEmail,
        afterIdentification: CheckoutRoute.OrderSummary,
      }
    )
  }

  return (
    <>
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <Switch>
        <Route path={CheckoutRoute.PickupIdentify}>
          <IdentificationStep
            onStepSubmit={handleIdentifySubmit}
            initialIdentification={clientData.identification}
          />
        </Route>
        <Route path={CheckoutRoute.PickupEmail}>
          <EmailStepContainer
            onStepSubmit={handleEmailSubmit}
            currentState={clientData}
          />
        </Route>
        <Route path={CheckoutRoute.PickupName}>
          <NameStep
            isEditing={isEditing}
            onStepSubmit={handleNameSubmit}
            initialName={initialName}
          />
        </Route>
        <Route path={CheckoutRoute.PickupDocument}>
          <DocumentStep
            isEditing={isEditing}
            onStepSubmit={handleDocumentSubmit}
            initialDocument={clientData.document}
            clientName={clientData.name}
          />
        </Route>
        <Route path={CheckoutRoute.PickupPhone}>
          <PhoneStep
            isEditing={isEditing}
            initialPhone={clientData.phone}
            clientName={clientData.name}
            onStepSubmit={handlePhoneSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.PickupClientProfile}>
          <ClientProfileContainer nextStep={handleConfirmClientSubmit} />
        </Route>
        <Route path={CheckoutRoute.PickupCompanyName}>
          <CompanyNameStep
            isEditing={isEditing}
            data-testid="company-name-pickup-step"
            onStepSubmit={handleCompanyNameSubmit}
            initialName={clientData.companyInformation?.companyName}
          />
        </Route>
        <Route path={CheckoutRoute.PickupTradeName}>
          <TradeNameStep
            isEditing={isEditing}
            data-testid="trade-name-pickup-step"
            onStepSubmit={handleTradeNameSubmit}
            initialName={clientData.companyInformation?.tradeName}
          />
        </Route>
        <Route path={CheckoutRoute.PickupCorporatePhone}>
          <CorporatePhoneStep
            isEditing={isEditing}
            onStepSubmit={handleCorporatePhoneSubmit}
            initialPhone={clientData.companyInformation?.corporatePhone}
          />
        </Route>
        <Route path={CheckoutRoute.PickupCorporateDocument}>
          <CorporateDocumentStep
            isEditing={isEditing}
            onStepSubmit={handleCorporateDocumentSubmit}
            initialDocument={clientData.companyInformation?.corporateDocument}
          />
        </Route>
        <Route path={CheckoutRoute.PickupStateInscription}>
          <StateInscriptionStep
            isEdit={isEditing}
            onStepSubmit={handleStateInscriptionSubmit}
            initialStateInscription={
              clientData.companyInformation.stateInscription
            }
          />
        </Route>
      </Switch>
    </>
  )
}
